import { useMemo } from "react";

import type { ICohortQueryModel } from "@performyard/models";
import type { GetReviewCycleDetailsResponseFrontend } from "@performyard/service-contracts";

import type { FormsByType } from "../steps/AddFormsStep/types";

import { useAllReviewForms } from "../../../../../api/hooks/defs/forms";
import { useReviewCycleDetails } from "../../../../../api/hooks/defs/review-cycle";
import { useCopySubjectSelection } from "./useCopySubjectSelection";
import { useReviewTemplate } from "./useReviewTemplate";

import type { CycleType } from "../Config";
import { type ReviewCycleCopySeed, buildCopyInitialData } from "../buildCopyInitialData";
import { templateAssignmentsToConfiguredForms } from "../steps/AddFormsStep/services/transformAssignments";
import { SUBJECTS_STEP_ID } from "../steps/SelectSubjectsStep/constants";

const EMPTY_FORMS: FormsByType = { standard: [], "360": [], external: [] };

type DetailsAutoflow = NonNullable<GetReviewCycleDetailsResponseFrontend["autoflow"]>;
type QueryUserTask = Extract<DetailsAutoflow["tasks"][number], { taskType: "query:user" }>;

// The details type pipeline only derives manual/automated, so resolve "group" from the group id.
const deriveCycleType = (details: GetReviewCycleDetailsResponseFrontend): CycleType =>
	details.type === "automated" ? "automated" : details.groupId ? "group" : "manual";

const toCopySeed = (details: GetReviewCycleDetailsResponseFrontend): ReviewCycleCopySeed => {
	const scheduled = details.autoflow?.trigger.type === "scheduled" ? details.autoflow.trigger.scheduled : undefined;
	const queryUserTask = details.autoflow?.tasks.find((task): task is QueryUserTask => task.taskType === "query:user");
	const automation =
		details.autoflow && queryUserTask
			? {
					type: scheduled?.recurring ? ("recurring" as const) : ("oneTime" as const),
					endDate: scheduled?.endTimeDate ?? null,
					userQuery: queryUserTask.config.query as ICohortQueryModel,
				}
			: null;

	return {
		type: deriveCycleType(details),
		name: details.name,
		startDate: details.startDate,
		adminsOnly: details.adminsOnly,
		group: details.groupId ? { id: details.groupId, name: details.groupName ?? "" } : null,
		template: details.lookupWorkflow ? { id: details.lookupWorkflow._id, name: details.lookupWorkflow.name } : null,
		automation,
	};
};

/**
 * Builds the Copy-flow `initialData` from the cycle's details (already cached from the details
 * page), the current template's forms, and (for manual cycles) a server-created subject selection.
 */
export const useCopyInitialData = (copyCycleId: string | null) => {
	const {
		data: details,
		isLoading: detailsLoading,
		isError: detailsFailed,
	} = useReviewCycleDetails(copyCycleId ?? "");

	const { data: templateData, isLoading: templateLoading } = useReviewTemplate(details?.lookupWorkflow?._id ?? null);
	const { data: formsData, isLoading: formsLoading } = useAllReviewForms();

	const isManual = !!details && deriveCycleType(details) === "manual";
	const {
		userSelectionId,
		selectedCount,
		isLoading: selectionLoading,
		isError: selectionFailed,
	} = useCopySubjectSelection(copyCycleId, !!copyCycleId && isManual);

	const initialData = useMemo(() => {
		if (!details) return undefined;
		const activeFormBaseIds = new Set((formsData?.data ?? []).map((form) => form.baseId));
		const templateForms = templateData
			? templateAssignmentsToConfiguredForms(
					templateData.formAssignments.filter((a) => activeFormBaseIds.has(String(a.formBaseId)))
				)
			: EMPTY_FORMS;
		const base = buildCopyInitialData(toCopySeed(details), templateForms);
		if (isManual && userSelectionId) {
			return { ...base, [SUBJECTS_STEP_ID]: { userSelectionId, selectedCount } };
		}
		return base;
	}, [details, templateData, formsData, isManual, userSelectionId, selectedCount]);

	return {
		cycleType: details ? deriveCycleType(details) : undefined,
		initialData,
		userSelectionId,
		isLoading: !!copyCycleId && (detailsLoading || templateLoading || formsLoading || selectionLoading),
		isError: !!copyCycleId && (detailsFailed || selectionFailed),
	};
};
